import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, CheckCircle, Loader2, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import FormField from '../ui/form-field';
import CompanyGalleryPhotos from './company-gallery-photos';
import CompanyLogoUpload from './company-logo-upload';
import { CompanyEditFormData } from "../../model/professional/compagny-model";
import companyEditFormViewModel from '../../view-model/professional/company/company-edit-form-view-model';

interface CompanyEditFormProps {
  isOpen: boolean;
  onSubmit: (data: CompanyEditFormData) => void;
  onClose: () => void;
}

const CompanyEditForm: React.FC<CompanyEditFormProps> = observer(({ isOpen, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const vm = companyEditFormViewModel;

  // Initialiser le formulaire au montage et nettoyer à la fermeture
  useEffect(() => {
    if (isOpen) {
      vm.initializeForm();
    } else {
      // Nettoyer quand le modal se ferme
      vm.cleanup();
    }
  }, [vm, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    vm.handleSubmit(e, onSubmit, t);
  };

  // Ne rien afficher si le modal n'est pas ouvert
  if (!isOpen) return null;

  if (vm.isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading', 'Chargement...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('company.form.editTitle', 'Modifier l\'entreprise')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Informations de base */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="businessName"
            name="businessName"
            label={t('company.form.businessName', 'Nom de l\'entreprise')}
            placeholder={String(t('company.form.businessNamePlaceholder', 'Ex: Salon de coiffure John Doe'))}
            defaultValue={vm.formData.businessName}
            onChange={vm.handleChange}
            required
            className={vm.errors.businessName ? 'error' : ''}
            help={vm.errors.businessName || undefined}
            success={vm.successMessages.businessName || undefined}
          />

          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-1">
              {t('company.form.siren', 'Numéro SIREN')}
              {vm.formData.sirenVerified && (
                <span className="inline-flex items-center">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className="ml-1 text-xs text-green-500 font-medium">{t('company.form.verified', 'Vérifié')}</span>
                </span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                id="siren"
                name="siren"
                type="text"
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 ${
                  vm.formData.sirenVerified
                    ? 'border-green-500 dark:border-green-700'
                    : vm.errors.siren
                      ? 'border-red-500 dark:border-red-700'
                      : 'border-gray-300 dark:border-gray-700'
                }`}
                placeholder="123456789"
                maxLength={9}
                value={vm.formData.siren}
                onChange={vm.handleChange}
                required
              />
              <button
                type="button"
                disabled={vm.isVerifyingSiren || !vm.formData.siren || vm.formData.siren.length !== 9 || vm.formData.sirenVerified}
                onClick={() => vm.verifySiren(t)}
                className={`px-3 py-2 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center min-w-[100px] ${
                  vm.isVerifyingSiren || !vm.formData.siren || vm.formData.siren.length !== 9 || vm.formData.sirenVerified
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {vm.isVerifyingSiren ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t('common.verifying', 'Vérification...')}
                  </>
                ) : (
                  t('common.verify', 'Vérifier')
                )}
              </button>
            </div>
            {vm.errors.siren && (
              <p className="text-xs text-red-500 mt-1">{vm.errors.siren}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('company.form.sirenHelp', 'Numéro SIREN à 9 chiffres pour vérifier votre entreprise')}
            </p>
          </div>
        </div>

        {/* Coordonnées de contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="phone"
            name="phone"
            label={t('company.form.phone', 'Téléphone')}
            placeholder="Ex: 0612345678"
            defaultValue={vm.formData.phone}
            onChange={vm.handleChange}
            className={vm.errors.phone ? 'error' : ''}
          />

          <FormField
            id="email"
            name="email"
            label={t('company.form.email', 'Email')}
            type="text"
            placeholder="Ex: contact@entreprise.com"
            defaultValue={vm.formData.email}
            onChange={vm.handleChange}
            required
            className={vm.errors.email ? 'error' : ''}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="domain"
            name="domain"
            label={t('company.form.domain', 'Domaine d\'activité')}
            placeholder={String(t('company.form.domainPlaceholder', 'Ex: Esthétique, Coiffure, Massage...'))}
            defaultValue={vm.formData.domain || ''}
            onChange={vm.handleChange}
            required
            className={vm.errors.domain ? 'error' : ''}
            help={vm.errors.domain || String(t('company.form.domainHelp', 'Le secteur d\'activité de votre entreprise'))}
          />

          <FormField
            id="description"
            name="description"
            label={t('company.form.description', 'Description')}
            type="textarea"
            placeholder={String(t('company.form.descriptionPlaceholder', 'Décrivez votre entreprise...'))}
            defaultValue={vm.formData.description}
            onChange={vm.handleChange}
            help={String(t('company.form.descriptionHelp', 'Cette description sera visible sur votre profil public'))}
          />
        </div>

        {/* Adresse */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="address"
            name="address"
            label={t('company.form.address', 'Adresse')}
            placeholder={String(t('company.form.addressPlaceholder', 'Ex: 123 Rue de Paris'))}
            defaultValue={vm.formData.address}
            onChange={vm.handleChange}
            required
            className={vm.errors.address ? 'error' : ''}
          />

          <FormField
            id="postalCode"
            name="postalCode"
            label={t('company.form.postalCode', 'Code postal')}
            placeholder="75001"
            defaultValue={vm.formData.postalCode}
            onChange={vm.handleChange}
            required
            className={vm.errors.postalCode ? 'error' : ''}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="city"
            name="city"
            label={t('company.form.city', 'Ville')}
            placeholder={String(t('company.form.cityPlaceholder', 'Ex: Paris'))}
            defaultValue={vm.formData.city}
            onChange={vm.handleChange}
            required
            className={vm.errors.city ? 'error' : ''}
          />

          <FormField
            id="country"
            name="country"
            label={t('company.form.country', 'Pays')}
            placeholder={String(t('company.form.countryPlaceholder', 'Ex: France'))}
            defaultValue={vm.formData.country}
            onChange={vm.handleChange}
            required
            className={vm.errors.country ? 'error' : ''}
          />
        </div>

        {/* Conditions de réservation */}
        <FormField
          id="reservationConditions"
          name="reservationConditions"
          label={t('company.form.reservationConditions', 'Conditions de réservation')}
          type="textarea"
          placeholder={String(t('company.form.reservationConditionsPlaceholder', 'Ex: Annulation gratuite jusqu\'à 24h avant...'))}
          defaultValue={vm.formData.reservationConditions}
          onChange={vm.handleChange}
          help={String(t('company.form.reservationConditionsHelp', 'Précisez vos conditions d\'annulation, de remboursement, etc.'))}
        />

        {/* Méthodes de paiement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('company.form.paymentMethods', 'Méthodes de paiement acceptées')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['Cash', 'Credit Card', 'Bank Transfer', 'Check', 'PayPal', 'Mobile Payment'].map((method) => (
              <div key={method} className="flex items-center">
                <input
                  type="checkbox"
                  id={`payment-${method}`}
                  checked={vm.formData.paymentMethods.includes(method)}
                  onChange={(e) => vm.handlePaymentMethodChange(method, e.target.checked)}
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor={`payment-${method}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t(`company.form.paymentMethod.${method.toLowerCase().replace(' ', '')}`, method)}
                </label>
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {t('company.form.paymentMethodsHelp', 'Sélectionnez les méthodes de paiement que vous acceptez')}
          </p>
        </div>

        {/* Logo */}
        <CompanyLogoUpload viewModel={vm} />

        {/* Galerie de photos */}
        <CompanyGalleryPhotos viewModel={vm} />

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            >
              <Save size={16} />
              <span>{t('company.form.update', 'Enregistrer')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default CompanyEditForm;
